import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, BookLock, Radio, ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { SignInRequired } from "@/components/SignInRequired";

const TITLE = "Admin Runbook — Progress & Realtime Access Rules | Inquira";
const DESC =
  "Internal Inquira runbook documenting the access rules for user_progress and Realtime delivery of security_scan_runs, plus how to validate policy behaviour after changes.";

export const Route = createFileRoute("/runbook")({
  component: RunbookPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

function Card({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3 rounded-2xl border border-rule bg-card p-5">
      <h2 className="flex items-center gap-2 text-lg font-extrabold text-ink">
        <span className="text-neon-pink">{icon}</span>
        {title}
      </h2>
      <div className="space-y-3 text-sm leading-relaxed text-ink-muted">{children}</div>
    </section>
  );
}

function Sql({ children }: { children: string }) {
  return (
    <pre className="overflow-x-auto rounded-xl border border-rule bg-background p-3 text-xs text-ink">
      <code>{children}</code>
    </pre>
  );
}

function RunbookPage() {
  const { user, isLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);

  useEffect(() => {
    if (!user) { setIsAdmin(false); return; }
    let active = true;
    supabase
      .rpc("has_role" as never, { _user_id: user.id, _role: "super_admin" } as never)
      .then(({ data }) => { if (active) setIsAdmin(data === true); });
    return () => { active = false; };
  }, [user]);

  if (isLoading) return <div className="p-8 text-center">Loading…</div>;
  if (!user) {
    return <SignInRequired title="Admin runbook" description="Sign in with an admin account to read the runbook." />;
  }
  if (isAdmin === null) return <div className="p-8 text-center">Checking access…</div>;
  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-md p-8 text-center">
        <h1 className="text-xl font-bold text-ink">Admins only</h1>
        <p className="mt-2 text-sm text-ink-muted">This runbook is restricted to Inquira super admins.</p>
        <Link to="/" className="mt-4 inline-block text-sm font-semibold text-neon-pink underline">Back home</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-5 px-4 py-8 sm:py-12">
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-ink">
        <ArrowLeft className="h-4 w-4" /> Admin dashboard
      </Link>
      <header>
        <h1 className="text-3xl font-bold tracking-tight text-ink">Access rules runbook</h1>
        <p className="mt-1 text-sm text-ink-muted">
          Authoritative reference for <code>user_progress</code> row access and Realtime delivery of
          <code> security_scan_runs</code>. Update this page in the same change as any policy migration.
        </p>
      </header>

      <Card icon={<BookLock className="size-5" />} title="user_progress — owner reads, server-only writes">
        <ul className="list-disc space-y-1 pl-5">
          <li>RLS is enabled. One PERMISSIVE SELECT policy allows <code>auth.uid() = user_id</code>.</li>
          <li>
            Writes are denied by three RESTRICTIVE per-command policies (INSERT / UPDATE / DELETE) with
            <code> false</code>. A single <code>FOR ALL … USING (false)</code> policy must never be used: it also
            blocks the owner&apos;s own SELECT, because every restrictive policy has to pass.
          </li>
          <li>
            INSERT / UPDATE / DELETE privileges are revoked from <code>anon</code> and <code>authenticated</code>.
            All progress mutations go through SECURITY DEFINER routines (progress engine RPCs).
          </li>
        </ul>
        <Sql>{`-- validate after any change
select polname, polcmd, polpermissive, pg_get_expr(polqual, polrelid) as using_expr
from pg_policy where polrelid = 'public.user_progress'::regclass;

-- expect: 1 permissive SELECT (auth.uid() = user_id)
--         3 restrictive policies for INSERT/UPDATE/DELETE only`}</Sql>
      </Card>

      <Card icon={<Radio className="size-5" />} title="Live updates — security_scan_runs">
        <ul className="list-disc space-y-1 pl-5">
          <li>
            The table is intentionally NOT in the <code>supabase_realtime</code> publication. Admin dashboards
            (security tab and findings diff) refresh by polling every 30s instead, because a
            <code> postgres_changes</code> channel would report SUBSCRIBED while never delivering rows.
          </li>
          <li>
            Reads stay super-admin-only: SELECT is gated on{" "}
            <code>has_role(auth.uid(), &apos;super_admin&apos;)</code>.
          </li>
          <li>Client writes are blocked by per-command RESTRICTIVE policies; only the scan job writes rows.</li>
        </ul>
        <Sql>{`-- expect ZERO rows: the table must not be published to Realtime
select * from pg_publication_tables
where pubname = 'supabase_realtime' and tablename = 'security_scan_runs';

select polname, polcmd, polpermissive from pg_policy
where polrelid = 'public.security_scan_runs'::regclass;`}</Sql>
      </Card>

      <Card icon={<ShieldCheck className="size-5" />} title="How to validate after a change">
        <ol className="list-decimal space-y-1 pl-5">
          <li>Run the SQL checks above and compare against the expectations listed on this page.</li>
          <li>
            Run the CI regression suite locally:
            <code> bunx vitest run src/test/progress-realtime-policies.test.ts</code>. It asserts the migration
            shape statically and, when Supabase env vars are present, that anon/owner writes are rejected and owner
            reads still work.
          </li>
          <li>
            Sign in as a non-admin and confirm a <code>security_scan_runs</code> Realtime subscription receives no
            payloads; sign in as a super admin and confirm live rows arrive in the admin security tab.
          </li>
          <li>Re-run the security scan from Admin → Security and confirm 0 unresolved findings.</li>
        </ol>
        <p>
          Reference documentation for public-facing policies lives at{" "}
          <Link to="/security" className="font-semibold text-neon-pink underline">/security</Link>.
        </p>
      </Card>
    </div>
  );
}